import { CREDIT_RESUME_ID_PARAM } from "@/utils/creditAuthIntentPersistence";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import { QUOTE_RESUME_ID_PARAM } from "@/utils/quoteResumePersistence";

export const CREDIT_CHECKOUT_RESTORE_ID_PARAM = "credit_restore_id";
export const CREDIT_CHECKOUT_HANDOFF_ERROR =
  "Your dress is still here. Allow site data, then try again.";

// Checkout restore ids are browser-local bearer capabilities. Accept only the
// 128-bit formats generated below so a caller cannot turn this parameter into
// an arbitrary SavePoint key (for example an auth or quote resume key).
const CREDIT_CHECKOUT_RESTORE_ID_PATTERN =
  /^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

const TRANSIENT_CREATE_PARAMS = [
  CREATE_AUTH_HANDOFF_PARAM,
  CREDIT_RESUME_ID_PARAM,
  QUOTE_RESUME_ID_PARAM,
  CREDIT_CHECKOUT_RESTORE_ID_PARAM,
  "credit_intent",
  "credit_pack",
  "credit_gate",
  "quote_intent",
  "quote_account",
  "restore_form",
  "credits_purchased",
  "_anon_work_adopted",
  "_ssr_oauth",
  // These parameters are authoritative prefill targets. The checkout
  // SavePoint already carries the ticket, images, view, and chat; leaving any
  // of them in the return URL makes initialization skip that richer state.
  "ticket_id",
  "modify",
  "gallery_design",
  // Exact session and fresh-start URLs are also authoritative. A scoped
  // browser draft must be the sole owner of an auth/checkout return instead
  // of racing one of these server/navigation targets.
  "session_id",
  "new",
] as const;

export function isValidCreditCheckoutRestoreId(
  restoreId: string | null | undefined
): restoreId is string {
  return Boolean(
    restoreId && CREDIT_CHECKOUT_RESTORE_ID_PATTERN.test(restoreId)
  );
}

export function createCreditCheckoutRestoreId(): string | null {
  try {
    if (typeof window === "undefined" || !window.crypto) return null;
    if (typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join(
      ""
    );
  } catch {
    return null;
  }
}

export function getCreditCheckoutRestoreId(
  urlOrHref: URL | string
): string | null {
  try {
    const url = typeof urlOrHref === "string" ? new URL(urlOrHref) : urlOrHref;
    const restoreId = url.searchParams.get(CREDIT_CHECKOUT_RESTORE_ID_PARAM);
    return isValidCreditCheckoutRestoreId(restoreId) ? restoreId : null;
  } catch {
    return null;
  }
}

export function sanitizeCreateReturnUrl(currentHref: string): URL {
  const url = new URL(currentHref);
  for (const param of TRANSIENT_CREATE_PARAMS) {
    url.searchParams.delete(param);
  }
  return url;
}

/**
 * Build the durable destination Stripe should return to after a credit
 * purchase. Auth and resume parameters are one-shot capabilities; carrying
 * them through checkout would replay already-consumed actions over the
 * restored design.
 */
export function buildCreditCheckoutReturnUrl(
  currentHref: string,
  options: {
    restoreForm?: boolean;
    creditsPurchased?: boolean;
    restoreId?: string | null;
  } = {}
): string {
  const url = sanitizeCreateReturnUrl(currentHref);
  const restoreId = isValidCreditCheckoutRestoreId(options.restoreId)
    ? options.restoreId
    : null;
  // A restore flag without its exact opaque id is unsafe: it would invite the
  // create page to fall back to another tab's canonical draft.
  if (options.restoreForm && restoreId) {
    url.searchParams.set("restore_form", "true");
    url.searchParams.set(CREDIT_CHECKOUT_RESTORE_ID_PARAM, restoreId);
  }
  if (options.creditsPurchased) {
    url.searchParams.set("credits_purchased", "true");
  }
  return url.toString();
}
